import * as React from 'react';
import { cn } from '../cn';

export interface PageHeaderProps {
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
  back?: React.ReactNode;
  className?: string;
  size?: 'lg' | 'md';
}
export function PageHeader({ title, eyebrow, description, actions, meta, back, className, size = 'lg' }: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-6', className)}>
      <div className="min-w-0">
        {back ? <div className="mb-2 text-[13px] text-text-2 [&_a]:inline-flex [&_a]:items-center [&_a]:gap-1 [&_a:hover]:text-text">{back}</div> : null}
        {eyebrow ? <div className="text-[12px] font-medium uppercase tracking-[0.08em] text-text-3 mb-1">{eyebrow}</div> : null}
        <h1 className={cn('font-semibold tracking-[-0.02em] text-text leading-tight flex items-center gap-3 flex-wrap', size === 'lg' ? 'text-[28px]' : 'text-[22px]')}>{title}</h1>
        {description ? <p className="text-[14px] text-text-2 mt-1 max-w-[64ch]">{description}</p> : null}
        {meta ? <div className="flex items-center gap-3 text-[13px] text-text-2 mt-2 flex-wrap">{meta}</div> : null}
      </div>
      {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
    </header>
  );
}

export function Toolbar({ className, children, sticky = true }: { className?: string; children: React.ReactNode; sticky?: boolean }) {
  return <div className={cn('glass flex items-center gap-2 flex-wrap px-4 py-2 rounded-[var(--radius)] shadow-[var(--shadow-xs)] mb-4', sticky && 'sticky top-2 z-20', className)}>{children}</div>;
}

export function Page({ children, className, width = 'default' }: { children: React.ReactNode; className?: string; width?: 'default' | 'narrow' | 'wide' | 'full' }) {
  const w = { default: 'max-w-[1120px]', narrow: 'max-w-[760px]', wide: 'max-w-[1440px]', full: 'max-w-none' }[width];
  return <div className={cn('mx-auto w-full px-6 py-6 lg:px-10 lg:py-8', w, className)}>{children}</div>;
}

export function Section({ title, action, children, className }: { title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('mb-8', className)}>
      {title ? (
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-text">{title}</h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}
